/**
 * The HIVE lockup: the hexagon mark plus the wordmark.
 *
 * Inline rather than an `<img>`, and filled with `currentColor` rather than a
 * baked hue. The site puts this on two surfaces that need opposite treatments
 * — the brand navy on the white header, white on the `deep` footer and hero —
 * and the vendor kit ships those as two separate files. One component that
 * inherits colour serves both, costs no request, and cannot drift out of sync
 * the way two exported files do.
 *
 * Geometry is the vendor SVG verbatim (`public/images/brand/logo-color.svg`),
 * transforms and all. The nested `<g>` matrices look redundant but they are
 * what the export produced, and re-deriving flat coordinates by hand is how a
 * mark quietly stops matching the print and email versions. Only two things
 * changed: `fill` became `currentColor`, and the viewBox was tightened to the
 * ink so the lockup can be positioned by its own edges instead of by the
 * export's padding.
 *
 * The brand navy lives in `--color-brand` and is `#283c63` — the value read
 * from the vendor file, not sampled from an image.
 */
export function Logo({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="471 270 2615 759"
      role="img"
      aria-label="HIVE"
      className={className}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="scale(8.620689655172415) translate(10, 10)">
        <g transform="matrix(2.666666666666667,0,0,2.666666666666667,18,0)">
          <path d="M24,41L10,32.5V16L24,8l14,8.25v16.5ZM13,31l11,6.48L35,31V18L24,11.53,13,18V31Z" />
        </g>
        <g transform="matrix(1.3389093984755032,0,0,1.3389093984755032,141.93273224467947,37.22220020307555)">
          <path d="M11.094 16.875 l0 -15.547 l-6.5625 0 l0 37.344 l6.5625 0 l0 -21.797 z M28.984 1.328000000000003 l0 15.547 l-11.406 0 l0 6.0938 l11.406 0 l0 15.703 l6.4844 0 l0 -37.344 l-6.4844 0 z M63.281 16.875 l0 -15.547 l-6.5625 0 l0 37.344 l6.5625 0 l0 -21.797 z M88.9844 1.328000000000003 l-7.0313 0 l13.594 37.266 l7.1094 0 l-7.8906 -22.344 z M117.73400000000001 1.328000000000003 l-6.875 0 l-8.125 19.844 l3.3594 8.9063 z M126.0938 1.4059999999999988 l27.813 0 l0 5.9375 l-27.813 0 l0 -5.9375 z M126.0938 32.6562 l27.734 0 l0 5.9375 l-27.734 0 l0 -5.9375 z M126.0938 16.875 l25.078 0 l0 5.8594 l-25.078 0 l0 -5.8594 z" />
        </g>
      </g>
    </svg>
  );
}

/** The mark alone — for the favicon, avatars, and anywhere the lockup is too wide. */
export function LogoMark({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="80 64 224 264"
      role="img"
      aria-label="HIVE"
      className={className}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="matrix(2.666666666666667,0,0,2.666666666666667,18,0)">
        <path d="M24,41L10,32.5V16L24,8l14,8.25v16.5ZM13,31l11,6.48L35,31V18L24,11.53,13,18V31Z" />
      </g>
    </svg>
  );
}
